import { ErrorMessage, Field, Form, Formik, FieldArray } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DiscoverInitialValue, EditDiscoverValidation } from "../../constant";
import {
  Confirmation,
  ErrorToast,
  SuccessToast,
} from "../../../../Utils/SweetAlert";
import InputWithLabel from "../../../../Components/InputWithLabel";
import TextArea from "../../../../Components/TextArea";
import {
  useMutateCreateImage,
  useMutateEditDiscover,
} from "../../hooks/useApiHook";
import { GetSingleDiscover } from "../../../../Api/discover";
import { useQuery } from "@tanstack/react-query";
// import "../../style.css";
import CustomSelect from "../../../../Utils/CustomSelect";
import ImageSelect from "../../../../Components/ImageSelect";
import selectIcon from "../../../../assets/Images/select-icon.png";
import { MdModeEditOutline } from "react-icons/md";
import IconUpload from "../../../../Components/SingleProfileSelect";
import DiscoverImageSelect from "../ImageUpload";
import BackButton from "../../../../Components/BackButton";
import SaveButton from "../../../../Components/SaveButton";
import PhaseSelect from "../../../../Components/PhaseSelect";

const EditDiscover = () => {
  const navigate = useNavigate();
  const formRef = useRef();
  const stateData = useLocation();
  const imageInputRef = useRef([]);
  const imageContainerRef = useRef([]);
  const dropAreaRef = useRef([]);
  const { id } = stateData?.state || "";
  const [isDscoverQuestionDeleted, setIsDscoverQuestionDeleted] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Api
   */
  const editDiscover = useMutateEditDiscover();
  const addImageApi = useMutateCreateImage();

  /**
   * Type options
   */
  const typeOptions = [
    {
      label: "Quick Lessons",
      value: "quick_lessons",
    },
    {
      label: "Suggested Topics",
      value: "suggested_topics",
    },
  ];

  /**
   * Subscription type options
   */
  const subscriptionTypeOptions = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Premium",
      value: "premium",
    },
    {
      label: "Free",
      value: "free",
    },
  ];

  const fetchDiscover = async () => {
    try {
      setLoading(true);
      const response = await GetSingleDiscover({ Id: id });
      setData(response);
    } catch (error) {
      ErrorToast(error?.response?.data?.message || "Server Error.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set the data of single record
   */
  useEffect(() => {
    if (data) {
      if (formRef?.current?.values) {
        formRef?.current?.setValues({
          icon: data?.data?.iconUrl || "",
          image: data?.data?.imageUrl || "",
          itemDescription: data?.data?.itemDescription || "",
          name: data?.data?.name || "",
          subTitle: data?.data?.subTitle || "",
          title: data?.data?.title || "",
          description: data?.data?.description || "",
          type: data?.data?.type || "",
          questions:
            data.data.discoverQuestions &&
              data.data.discoverQuestions.length > 0
              ? data?.data?.discoverQuestions?.map((item) => {
                return {
                  id: item?._id,
                  question: item?.question,
                  image: item?.imageUrl || "",
                  order: item?.order,
                  answers: item?.answers?.map((value) => {
                    return {
                      id: value?._id,
                      answer: value?.answer,
                      correct: value?.correct,
                    };
                  }),
                };
              })
              : null,
          phase: data?.data?.phase || "",
          subscriptionType: data?.data?.subscriptionType || "all",
        });
      }
    }
  }, [data]);

  useEffect(() => {
    if (id) {
      fetchDiscover();
    }
  }, [id])

  /**
   * Used to trigger file input
   */
  const triggerFileInput = () => {
    imageInputRef.current.click();
  };

  /**
   * Used to get last name from image url
   * @param {string} url "Url of image file"
   * @returns "Return spliced name from image url"
   */
  function getLastNameFromUrl(fullUrl) {
    try {
      const url = new URL(fullUrl);
      return url.pathname.replace(/^\/+/, '').replace("uploads/", "");
    } catch (error) {
      return fullUrl;
    }
  }

  /**
   * Used to handle submit form
   * @param {any} values "Values of form fields"
   */
  const handleSubmit = async (values) => {
    const reqData = {
      id: id,
      name: values?.name,
      title: values?.title,
      subTitle: values?.subTitle || "",
      description: values?.description,
      itemDescription: values?.itemDescription,
      icon: values?.icon ? getLastNameFromUrl(values?.icon) : "",
      image: values?.image ? getLastNameFromUrl(values?.image) : "",
      type: values?.type,
      phase: values?.phase,
      subscriptionType: values?.subscriptionType || "all",
      questions:
        values?.questions && values.questions.length > 0
          ? values?.questions?.map((item, index) => {
            return {
              id: item?.id,
              question: item?.question,
              image: item?.image ? getLastNameFromUrl(item.image) : "",
              order: index + 1,
              answers: item?.answers?.map((value) => {
                return {
                  id: value?.id,
                  answer: value?.answer,
                  correct: value?.correct,
                };
              }),
            };
          })
          : [],

      isDscoverQuestionDeleted: isDscoverQuestionDeleted,
    };

    setIsLoading(true);
    editDiscover.mutate(reqData, {
      onSuccess: (response) => {
        if (response?.status != 0) {
          SuccessToast(response?.message || "Discover updated.");
          navigate("/admin/discover-listing");
        } else {
          ErrorToast(response?.message || "Server error.");
        }
        setIsLoading(false);
      },
      onError: (error) => {
        ErrorToast(error?.response?.data?.message || "Server Error.");
        setIsLoading(false);
      },
    });
  };

  const removeQuestionAnswer = (questionData, questionIndex, answerIndex) => {
    if (questionData?.length) {
      const questionDetails = questionData[questionIndex];
      if (questionDetails && questionDetails?.answers?.length > 2) {
        let questionAnswers = [];
        questionDetails?.answers.forEach((item, index) => {
          if (index !== answerIndex) {
            questionAnswers.push(item);
          }
        });

        questionDetails.answers = questionAnswers;
      }
    }
    return questionData;
  };

  return (
    <>
      <Helmet>
        <title>OVE | Admin Edit Discover</title>
      </Helmet>

      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card card-border overflow-visible">
                <div className="card-body">
                  <div className="page-header d-block">
                    <div className="row">
                      <div className="col">
                        <h3 className="page-title">Edit Discover</h3>
                      </div>
                      <div className="col-auto d-flex align-items-center justify-content-center">
                        <BackButton to="/admin/discover-listing" />
                      </div>
                    </div>
                  </div>
                  <Formik
                    initialValues={DiscoverInitialValue}
                    validationSchema={EditDiscoverValidation}
                    validateOnChange
                    innerRef={formRef}
                    onSubmit={handleSubmit}
                  >
                    {(formik) => {
                      const { values, setFieldValue, setFieldTouched } = formik;
                      return (
                        <Form>
                          <div className="section-border">
                            <div className="row">
                              <div className="col-3">
                                <label>
                                  Icon
                                  <span className="text-danger">*</span>
                                </label>
                                <IconUpload
                                  values={values}
                                  setFieldValue={setFieldValue}
                                  addImageApi={addImageApi}
                                  selectIcon={selectIcon}
                                />
                              </div>

                              <div className="col-9">
                                <div className="row">
                                  <div className="col-12">
                                    <Field
                                      Label="title"
                                      type="text"
                                      name="title"
                                      placeholder="Enter Title"
                                      component={InputWithLabel}
                                    />
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-6">
                                    <Field
                                      Label="Name"
                                      type="text"
                                      name="name"
                                      placeholder="Enter Name"
                                      component={InputWithLabel}
                                    />
                                  </div>
                                  <div className="col-6">
                                    <label htmlFor="type">
                                      Type{" "}
                                    </label>
                                    <CustomSelect
                                      name="type"
                                      placeholder="Select Type"
                                      options={typeOptions}
                                      onChange={(option) =>
                                        setFieldValue("type", option.value)
                                      }
                                      value={typeOptions?.find(
                                        (item) => item.value === values.type
                                      )}
                                      styles={{
                                        option: (base, { isFocused, isSelected }) => ({
                                          ...base,
                                          backgroundColor: isSelected
                                            ? "#AD5ADC"
                                            : isFocused
                                              ? "#E8DBF3"
                                              : base.backgroundColor,
                                          color: isSelected ? "#fff" : "#333",
                                        }),
                                      }}
                                    />
                                    <ErrorMessage
                                      name="type"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-6">
                                    <label htmlFor="phase">
                                      Phase <span className="text-danger">*</span>
                                    </label>
                                    <PhaseSelect
                                      value={values.phase}
                                      onChange={(phase) =>
                                        setFieldValue("phase", phase)
                                      }
                                      onBlur={() =>
                                        setFieldTouched("phase", true)
                                      }
                                      isClearable={false}
                                      selectStyles={{
                                        control: (styles) => ({
                                          ...styles,
                                          backgroundColor: "#F7F5F5",
                                          borderRadius: "10px",
                                        }),
                                        menu: (styles) => ({
                                          ...styles,
                                          backgroundColor: "#F7F5F5",
                                          borderRadius: "10px",
                                          overflow: "hidden",
                                        }),
                                      }}
                                    />
                                    <ErrorMessage
                                      name="phase"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                  <div className="col-6">
                                    <label htmlFor="subscriptionType">
                                      Subscription type <span className="text-danger">*</span>
                                    </label>
                                    <CustomSelect
                                      name="subscriptionType"
                                      placeholder="Select Subscription Type"
                                      options={subscriptionTypeOptions}
                                      onChange={(option) =>
                                        setFieldValue("subscriptionType", option.value)
                                      }
                                      value={subscriptionTypeOptions?.find(
                                        (item) => item.value === values.subscriptionType
                                      )}
                                      styles={{
                                        option: (base, { isFocused, isSelected }) => ({
                                          ...base,
                                          backgroundColor: isSelected
                                            ? "#AD5ADC"
                                            : isFocused
                                              ? "#E8DBF3"
                                              : base.backgroundColor,
                                          color: isSelected ? "#fff" : "#333",
                                        }),
                                      }}
                                    />
                                    <ErrorMessage
                                      name="subscriptionType"
                                      component="div"
                                      className="text-danger"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row dis-spacing">
                              <div className="col-4">
                                <Field
                                  Label="Image"
                                  name="image"
                                  component={DiscoverImageSelect}
                                  require={true}
                                />
                              </div>
                              <div className="col-8">
                                <Field
                                  Label="Item Description"
                                  name="itemDescription"
                                  placeholder="Enter Item Description"
                                  component={TextArea}
                                  style={{ height: "220px" }}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <FieldArray
                                name="description"
                                render={(arrayHelpers) => (
                                  <>
                                    <div className="row">
                                      {values.description.map(
                                        (description, index) => (
                                          <div className="d-flex" key={index}>
                                            <div className="col-9">
                                              <Field
                                                Label="Description"
                                                type="text"
                                                name={`description.${index}.description`}
                                                placeholder="Enter Description"
                                                component={TextArea}
                                                className="discover-description"
                                                style={{ height: "80px" }}
                                                handleChange={(e) => {
                                                  setFieldValue(
                                                    `description.${index}.description`,
                                                    e.value
                                                  );
                                                }}
                                              />
                                            </div>

                                            <div className="col-2 pr-0 ">
                                              <button
                                                type="button"
                                                className="description-add-button"
                                                onClick={() =>
                                                  arrayHelpers.push("")
                                                }
                                              >
                                                Add
                                              </button>
                                            </div>

                                            {index > 0 && (
                                              <div className="col-2 pl-0 pr-0">
                                                <button
                                                  type="button"
                                                  className="description-remove-button"
                                                  onClick={() =>
                                                    arrayHelpers.remove(index)
                                                  }
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <FieldArray name="questions">
                              {({ push, remove }) => (
                                <div>
                                  <div
                                    className={
                                      values?.questions?.length > 4
                                        ? "discover-specing"
                                        : "border-section-que-btn"
                                    }
                                  >
                                    <div className="row">
                                      <div className="col-6">
                                        <h4 className="question">Questions</h4>
                                      </div>
                                      <div className="col-6">
                                        <div className="text-end">
                                          <button
                                            className="question-add-button"
                                            type="button"
                                            onClick={() => {
                                              if (
                                                values?.questions?.length ==
                                                undefined
                                              ) {
                                                for (let i = 0; i < 5; i++) {
                                                  push({
                                                    question: "",
                                                    image: "",
                                                    answers: [
                                                      {
                                                        answer: "",
                                                        correct: false,
                                                      },
                                                      {
                                                        answer: "",
                                                        correct: false,
                                                      },
                                                    ],
                                                  });
                                                }
                                              } else if (
                                                values?.questions?.length > 4
                                              ) {
                                                setFieldValue(
                                                  "questions",
                                                  null
                                                );
                                                setIsDscoverQuestionDeleted(true);
                                              }
                                            }}
                                          >
                                            {values?.questions?.length ==
                                              undefined
                                              ? "Add Questions"
                                              : "Remove Questions"}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    className={
                                      values?.questions?.length > 4
                                        ? "section-border-addquestion"
                                        : ""
                                    }
                                  >
                                    {values?.questions?.map(
                                      (question, questionIndex) => (
                                        <div
                                          className={`${questionIndex < 4
                                            ? "border-question"
                                            : ""
                                            }`}
                                          key={questionIndex}
                                        >
                                          <div className="row">
                                            <div className="col-md-12">
                                              <Field
                                                Label={`Question ${questionIndex + 1
                                                  }`}
                                                type="text"
                                                name={`questions[${questionIndex}].question`}
                                                placeholder="Enter Question"
                                                component={TextArea}
                                                style={{
                                                  height: "80px",
                                                  overflow: "hidden",
                                                  resize: "none",
                                                }}
                                                className="discover-Question-description"
                                              />
                                            </div>
                                          </div>

                                          <div className="row mt-3 mb-3">
                                            <div className="col-md-6">
                                              <Field
                                                Label="Image"
                                                name={`questions[${questionIndex}].image`}
                                                component={DiscoverImageSelect}
                                                require={true}
                                              />
                                            </div>
                                          </div>

                                          <FieldArray
                                            name={`questions[${questionIndex}].answers`}
                                          >
                                            {({
                                              push: pushMember,
                                              remove: removeMember,
                                            }) => (
                                              <div>
                                                {question?.answers?.map(
                                                  (member, memberIndex) => {
                                                    if (memberIndex % 2 === 0) {
                                                      return (
                                                        <div
                                                          className="row"
                                                          key={memberIndex}
                                                        >
                                                          <div className="col-md-6">
                                                            <Field
                                                              Label={`Answer ${memberIndex + 1
                                                                }`}
                                                              type="text"
                                                              name={`questions[${questionIndex}].answers[${memberIndex}].answer`}
                                                              placeholder="Enter Answer"
                                                              component={
                                                                TextArea
                                                              }
                                                              className="discover-description"
                                                              style={{
                                                                height: "80px",
                                                              }}
                                                            />
                                                            <svg
                                                              stroke="currentColor"
                                                              fill="currentColor"
                                                              stroke-width="0"
                                                              viewBox="0 0 24 24"
                                                              height="18px"
                                                              width="18px"
                                                              xmlns="http://www.w3.org/2000/svg"
                                                              className="remove-ans-icon"
                                                              onClick={() => {
                                                                const removeQuestionData =
                                                                  removeQuestionAnswer(
                                                                    values?.questions,
                                                                    questionIndex,
                                                                    memberIndex
                                                                  );
                                                                if (
                                                                  removeQuestionData
                                                                ) {
                                                                  setFieldValue(
                                                                    `questions`,
                                                                    removeQuestionData
                                                                  );
                                                                  values.questions =
                                                                    removeQuestionData;
                                                                }
                                                              }}

                                                            >
                                                              <g id="Circle_Remove">
                                                                <g>
                                                                  <path d="M9.525,13.765a.5.5,0,0,0,.71.71c.59-.59,1.175-1.18,1.765-1.76l1.765,1.76a.5.5,0,0,0,.71-.71c-.59-.58-1.18-1.175-1.76-1.765.41-.42.82-.825,1.23-1.235.18-.18.35-.36.53-.53a.5.5,0,0,0-.71-.71L12,11.293,10.235,9.525a.5.5,0,0,0-.71.71L11.293,12Z"></path>
                                                                  <path d="M12,21.933A9.933,9.933,0,1,1,21.934,12,9.945,9.945,0,0,1,12,21.933ZM12,3.067A8.933,8.933,0,1,0,20.934,12,8.944,8.944,0,0,0,12,3.067Z"></path>
                                                                </g>
                                                              </g>
                                                            </svg>
                                                          </div>
                                                          {question?.answers[
                                                            memberIndex + 1
                                                          ] && (
                                                              <div className="col-md-6">
                                                                <Field
                                                                  Label={`Answer ${memberIndex +
                                                                    2
                                                                    }`}
                                                                  type="text"
                                                                  name={`questions[${questionIndex}].answers[${memberIndex +
                                                                    1
                                                                    }].answer`}
                                                                  placeholder="Enter Answer"
                                                                  component={
                                                                    TextArea
                                                                  }
                                                                  className="discover-description"
                                                                  style={{
                                                                    height:
                                                                      "80px",
                                                                  }}
                                                                />
                                                                <svg
                                                                  stroke="currentColor"
                                                                  fill="currentColor"
                                                                  strokeWidth="0"
                                                                  viewBox="0 0 24 24"
                                                                  height="18px"
                                                                  width="18px"
                                                                  xmlns="http://www.w3.org/2000/svg"
                                                                  className="remove-ans-icon"
                                                                  onClick={() => {
                                                                    const removeQuestionData =
                                                                      removeQuestionAnswer(
                                                                        values?.questions,
                                                                        questionIndex,
                                                                        memberIndex
                                                                      );
                                                                    if (
                                                                      removeQuestionData
                                                                    ) {
                                                                      setFieldValue(
                                                                        `questions`,
                                                                        removeQuestionData
                                                                      );
                                                                    }
                                                                  }}

                                                                >
                                                                  <g id="Circle_Remove">
                                                                    <g>
                                                                      <path d="M9.525,13.765a.5.5,0,0,0,.71.71c.59-.59,1.175-1.18,1.765-1.76l1.765,1.76a.5.5,0,0,0,.71-.71c-.59-.58-1.18-1.175-1.76-1.765.41-.42.82-.825,1.23-1.235.18-.18.35-.36.53-.53a.5.5,0,0,0-.71-.71L12,11.293,10.235,9.525a.5.5,0,0,0-.71.71L11.293,12Z"></path>
                                                                      <path d="M12,21.933A9.933,9.933,0,1,1,21.934,12,9.945,9.945,0,0,1,12,21.933ZM12,3.067A8.933,8.933,0,1,0,20.934,12,8.944,8.944,0,0,0,12,3.067Z"></path>
                                                                    </g>
                                                                  </g>
                                                                </svg>
                                                              </div>
                                                            )}
                                                        </div>
                                                      );
                                                    }
                                                    return null;
                                                  }
                                                )}
                                                <div className="col-md-12 pl-0 mt-3">
                                                  <button
                                                    className="question-add-button"
                                                    type="button"
                                                    onClick={() =>
                                                      pushMember({
                                                        answer: "",
                                                        correct: false,
                                                      })
                                                    }
                                                  >
                                                    <svg
                                                      stroke="currentColor"
                                                      fill="#FFF"
                                                      strokeWidth="0"
                                                      viewBox="0 0 448 512"
                                                      height="15px"
                                                      width="20px"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"></path>
                                                    </svg>
                                                    Add
                                                  </button>
                                                </div>

                                                <div className="row mt-2">
                                                  <label>
                                                    Correct{" "}
                                                    <span className="text-danger">
                                                      *
                                                    </span>
                                                    :
                                                  </label>
                                                  {question?.answers?.map(
                                                    (_, memberIndex) => (
                                                      <div
                                                        className="col-md-2" // Use mb-3 for bottom margin and col-md-2 to fit 6 in a row
                                                        key={memberIndex}
                                                      >
                                                        <div className="d-flex align-items-center">
                                                          <Field
                                                            type="checkbox"
                                                            className="checkbox-spacing me-2" // Add me-2 for right margin between checkbox and label
                                                            id={`correct-${questionIndex}-${memberIndex}`}
                                                            onChange={(
                                                              event
                                                            ) => {
                                                              const checked =
                                                                event
                                                                  .currentTarget
                                                                  .checked;
                                                              const newAnswers =
                                                                [
                                                                  ...values
                                                                    .questions[
                                                                    questionIndex
                                                                  ].answers,
                                                                ];
                                                              newAnswers.forEach(
                                                                (
                                                                  answer,
                                                                  index
                                                                ) => {
                                                                  if (
                                                                    index !==
                                                                    memberIndex
                                                                  ) {
                                                                    answer.correct = false;
                                                                  }
                                                                }
                                                              );
                                                              newAnswers[
                                                                memberIndex
                                                              ].correct =
                                                                checked;
                                                              setFieldValue(
                                                                `questions[${questionIndex}].answers`,
                                                                newAnswers
                                                              );
                                                            }}
                                                            checked={
                                                              values.questions[
                                                                questionIndex
                                                              ].answers[
                                                                memberIndex
                                                              ].correct
                                                            }
                                                            name={`questions[${questionIndex}].answers[${memberIndex}].correct`}
                                                          />
                                                          <label
                                                            className="checked-ans"
                                                            htmlFor={`correct-${questionIndex}-${memberIndex}`}
                                                          >
                                                            {`Answer ${memberIndex + 1
                                                              }`}
                                                          </label>
                                                        </div>
                                                        <ErrorMessage
                                                          name={`questions[${questionIndex}].answers[${memberIndex}].correct`}
                                                          component="div"
                                                          className="text-danger"
                                                        />
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </FieldArray>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </FieldArray>
                          </div>

                          <SaveButton extraClassName="mt-2" loading={isLoading} />
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditDiscover;
